import { FC, useEffect, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { Box, Divider, Grid, Stack, Typography } from '@mui/material';
import { debounce } from '@npi/buddy-utils';
import { FormikProps, useFormikContext } from 'formik';

import { NPIAutoComplete } from 'src/components/FormikComponents';
import {
    CurrencyInputFieldLabelledLayout,
    NPICurrencyInputFieldLabelled,
} from 'src/components/FormikComponents/NPICurrencyInputField/NPICurrencyInputFieldLabelled';
import MainCard from 'src/components/MainCard/MainCard';
import RiskLevels, { RiskLevel } from 'src/components/RiskLevels/RiskLevels';
import { showErrorToast } from 'src/components/Toasts/toast';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { useAuthContext } from 'src/contexts/AuthProvider';
import { useServiceOrderContext } from 'src/contexts/ServiceOrderProvider';
import { GET_MCCS } from 'src/graphql/queries/mccs.query';
import { isApplicationEditable } from 'src/pages/MerchantApplicationPage/merchantApplicationHelpers.util';

export interface SalesConditionsProps {
    formik: FormikProps<any>;
}
export const SalesConditions: FC<SalesConditionsProps> = () => {
    const { setPartnerId } = useServiceOrderContext();
    const { setFieldValue, getFieldProps, errors, touched } = useFormikContext();
    const { usersCurrency } = useAuthContext();
    const [mccData, setMccData] = useState([]);
    const [getMccs] = useLazyQuery(GET_MCCS, {
        fetchPolicy: 'network-only',
        onError: (error) => {
            return showErrorToast(error.message);
        },
    });
    const { merchantApplication } = useAddMerchantContext();
    const makeRequest = async () => {
        return getMccData();
    };

    useEffect(() => {
        makeRequest();
        setPartnerId(merchantApplication?.partnerId);
    }, []);

    const getMccData = debounce(async () => {
        const mccs = await getMccs();
        if (mccs) {
            const { data } = mccs.data.mccs;
            setMccData(data);
        }
    }, 1000);

    const mccs = useMemo(() => {
        if (mccData.length) {
            return mccData.map(({ id, name, code, risk, documents }) => {
                return {
                    id,
                    label: `${code} - ${name}`,
                    value: id.toString() || '',
                    code,
                    risk,
                    documents,
                };
            });
        }
        return [];
    }, [mccData]);

    const getRiskByValue = (value, dataArray) => {
        const foundItem = dataArray.find((item) => {
            return item.value === value;
        });

        if (foundItem) {
            return foundItem.risk;
        }
        return '';
    };

    const mccValue = getFieldProps('salesConditions.merchantCategoryCode').value;

    const mccFieldValue = getRiskByValue(mccValue?.value, mccs);

    const isEditingDisabled = isApplicationEditable(merchantApplication);

    const monthlyTurnoverError = touched?.salesConditions?.monthlyTurnover
        ? errors?.salesConditions?.monthlyTurnover
        : undefined;

    return (
        <>
            <MainCard>
                <Typography fontSize="24px" color="#575757" marginBottom="8px">
                    Merchant Category Code/Turnover GPV
                </Typography>
                <Typography fontSize="14px" color="#575757" marginBottom="8px">
                    Set merchant service requirements
                </Typography>
                <Divider
                    sx={{
                        borderColor: 'warning.main',
                        marginBottom: '34px',
                    }}
                />
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Stack>
                            <NPIAutoComplete
                                header="Merchant Category Code"
                                name="salesConditions.merchantCategoryCode"
                                options={mccs.map((m) => {
                                    return {
                                        id: m.id,
                                        label: m.label,
                                        value: m.value,
                                        risk: m.risk,
                                    };
                                })}
                                placeholder="Select Merchant Category Code"
                                disabled={isEditingDisabled}
                            />
                        </Stack>
                        <RiskLevels level={mccFieldValue || 'NONE'} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {mccFieldValue &&
                            mccFieldValue?.risk !== RiskLevel.LOW &&
                            mccFieldValue?.documents &&
                            mccFieldValue?.documents !== 'N/A' && (
                                <>
                                    <Typography
                                        variant="h4"
                                        fontSize="14px"
                                        color="grey.800"
                                        marginBottom="8px"
                                    >
                                        Supporting Documents
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            height: '100%',
                                            border: '1px solid #F39E4E',
                                            borderRadius: '12px',
                                            padding: '16px',
                                        }}
                                    >
                                        <Typography variant="body1" textAlign="left">
                                            {mccFieldValue?.documents}
                                        </Typography>
                                    </Box>
                                </>
                            )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <NPICurrencyInputFieldLabelled
                            header="Monthly Turnover/GPV"
                            fieldName="salesConditions.monthlyTurnover"
                            defaultValueInCents={
                                merchantApplication?.serviceRequirements?.salesConditions
                                    ?.monthlyTurnover ?? 0
                            }
                            error={Boolean(monthlyTurnoverError)}
                            errorMessage={monthlyTurnoverError}
                            setFieldValue={setFieldValue}
                            isDisabled={isEditingDisabled}
                            dimensions={{
                                width: '100%',
                                height: '50px',
                                borderRadius: '4px',
                            }}
                            layout={CurrencyInputFieldLabelledLayout.VERTICAL}
                            textStyle={{ fontWeight: 600 }}
                        />
                    </Grid>
                </Grid>
            </MainCard>
        </>
    );
};
